import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../hooks/useConfirm';
import LogoMark from '../../Image/logo-mark.png';
import { financialYear } from '../../utils/format';

function getInitials(name?: string): string {
  if (!name) return 'U';
  const parts = name.split(/[.\s_@-]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || 'U';
}

export default function Navbar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setIsProfileMenuOpen(false);
    const confirmed = await confirm({
      title: 'Sign Out',
      message: 'Are you sure you want to sign out?',
      confirmText: 'Sign Out',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;
    logout();
    navigate('/login');
  };

  return (
    <header className="relative z-40 shrink-0 bg-brand-950 text-white shadow-bar">
      <div className="h-[58px] lg:h-[62px] px-3 sm:px-4 lg:px-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            onClick={() => window.dispatchEvent(new Event('sidebarMobileOpen'))}
            className="lg:hidden w-10 h-10 rounded-md bg-white/10 hover:bg-white/15 text-white flex items-center justify-center transition-colors"
            aria-label="Open menu"
          >
            <i className="fas fa-bars text-sm"></i>
          </button>

          <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0">
            <div className="hidden sm:flex h-9 w-9 rounded-md bg-white p-1 shrink-0">
              <img src={LogoMark} alt="ASGenix" className="h-full w-full object-contain" />
            </div>
            <div className="min-w-0">
              <p className="text-[13px] sm:text-sm font-extrabold tracking-tight truncate leading-tight">
                ASGenix <span className="text-accent-400">Account</span>
              </p>
              <p className="text-[10px] sm:text-[11px] text-white/55 font-medium tracking-[0.12em] uppercase truncate">
                Finance Console
              </p>
            </div>
          </Link>
        </div>

        <div className="hidden md:flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-400 shrink-0" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/45 leading-none mb-0.5">
                Financial Year
              </p>
              <p className="text-xs font-semibold text-white/95">FY {financialYear()}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5 shrink-0">
          <div className="hidden sm:block text-right max-w-[200px]">
            <p className="text-xs font-semibold truncate text-white/95">{user?.name}</p>
            <p className="text-[11px] text-white/50 capitalize truncate">{user?.role?.toLowerCase()}</p>
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsProfileMenuOpen((o) => !o)}
              className="w-9 h-9 rounded-md bg-accent-500 text-brand-950 flex items-center justify-center text-xs font-extrabold shadow-soft"
              title={user?.email}
              aria-haspopup="menu"
              aria-expanded={isProfileMenuOpen}
            >
              {getInitials(user?.name)}
            </button>

            {isProfileMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setIsProfileMenuOpen(false)} aria-hidden="true" />
                <div className="absolute right-0 top-full mt-2 w-56 rounded-lg bg-white text-ink-900 shadow-2xl border border-ink-200 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-ink-100">
                    <p className="text-sm font-semibold truncate">{user?.name}</p>
                    <p className="text-xs text-ink-500 truncate">{user?.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      navigate('/settings?tab=password');
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50 transition-colors"
                  >
                    <i className="fas fa-key text-xs"></i>
                    Change password
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <i className="fas fa-right-from-bracket text-xs"></i>
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog />

      {/* Orange accent line — ASGenix brand */}
      <div className="h-0.5 bg-gradient-to-r from-brand-500 via-accent-500 to-accent-600" />
    </header>
  );
}
